import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { ProductTypeEnum, UserProfile, UserRepository } from '../core';
import { BaseService } from './base.service';

/**
* This service handles user profile data
*/
@Injectable()
export class UserService extends BaseService {

	private _currentUser$: BehaviorSubject<UserProfile> = new BehaviorSubject<UserProfile>(null);

	private readonly CUSTOMER_CARE: string = 'CustomerCare';
	private readonly ADMIN: string = 'Admin';

	constructor(private userRepo: UserRepository) {
		super();
	}

	/**
	* Get user first name and last name.
	*/
	getUserName(): Observable<{ firstName: string, lastName: string }> {
		return this.getUserProfile().pipe(filter(user => !!user), map(user => ({ firstName: user.firstName, lastName: user.lastName })));
	}

	/**
	* Get user country code.
	*/
	getUserCountry(): Observable<string> {
		return this.getCurrentUser().pipe(filter(user => !!user), map(user => user.country.code));
	}

	/**
	* Get current user.
	*/
	getCurrentUser(): Observable<UserProfile> {
		return this._currentUser$.asObservable().pipe(filter(user => !!user), first());
	}

	/**
	* Check if user is demo
	*/
	isDemoUser(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			filter(user => !!user && !!user.products),
			map(user => user.products.some(p => p.type == ProductTypeEnum.Fitbone && p.demo))
		);
	}

	/**
	* Check if user is Customer Care
	*/
	isCustomerCare(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			filter(user => !!user && !!user.roles),
			map(user => user.roles.includes(this.CUSTOMER_CARE))
		);
	}

	/**
	 * Check if user has fitbone grant
	 */
	hasFitboneGrant(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			filter(user => !!user && !!user.products),
			map(user => user.products.some(p => p.type == ProductTypeEnum.Fitbone))
		);
	}

	private getUserProfile(): Observable<UserProfile> {
		return this.userRepo.getUserProfile().pipe(
			map(res => this.handleApiResponse(res)),
			tap(user => this._currentUser$.next(user))
		);
	}

}
